import React, { useContext, useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { CacheContext, CacheContextType } from '../context/CacheContext';
import { Paper, Box, InputBase, Divider, Typography, CircularProgress } from '@mui/material';
import { Link } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';

interface SearchResults {
  users: Array<{ id: string; username: string; bio?: string }>;
  feeds: Array<{ id: string; feed_tag: string }>;
  posts: Array<{ id: string; value: string; user_record_id: string }>;
}

async function searchAll(query: string): Promise<SearchResults> {
  if (!query.trim()) return { users: [], feeds: [], posts: [] };

  const [usersResult, feedsResult, postsResult] = await Promise.all([
    supabase
      .from('users')
      .select('id, username, bio')
      .textSearch('search_vector', query, { type: 'websearch' })
      .limit(5),
    supabase
      .from('feeds')
      .select('id, feed_tag')
      .textSearch('search_vector', query, { type: 'websearch' })
      .limit(5),
    supabase
      .from('posts')
      .select('id, value, user_record_id')
      .textSearch('search_vector', query, { type: 'websearch' })
      .limit(5),
  ]);

  return {
    users: (usersResult.data ?? []) as any[],
    feeds: (feedsResult.data ?? []) as any[],
    posts: (postsResult.data ?? []) as any[],
  };
}

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

function SearchBar({
  parentRef,
}: {
  parentRef: React.RefObject<HTMLDivElement>;
}) {
  const getUserRecord = useContext<CacheContextType>(CacheContext).getUserRecord;
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResults | null>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const debouncedQuery = useDebounce(query, 300);

  useEffect(() => {
    if (!debouncedQuery.trim()) {
      setResults(null);
      setIsSearching(false);
      return;
    }
    setIsSearching(true);
    searchAll(debouncedQuery).then((r) => {
      setResults(r);
      setIsSearching(false);
    });
  }, [debouncedQuery]);

  const hasResults =
    results &&
    (results.users.length > 0 ||
      results.feeds.length > 0 ||
      results.posts.length > 0);

  const hasNoResults =
    results &&
    results.users.length === 0 &&
    results.feeds.length === 0 &&
    results.posts.length === 0;

  const closeResults = () => {
    setQuery('');
    setResults(null);
  };

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: isFocused ? 'primary.main' : 'divider',
          borderRadius: 2,
          px: 1.5,
          py: 0.5,
        }}
      >
        <SearchIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 20, flexShrink: 0 }} />
        <InputBase
          placeholder="Search users, posts, feeds..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => {
            setIsFocused(false);
            // Delay closing so click events on results fire first
            setTimeout(() => setResults(null), 200);
          }}
          fullWidth
          sx={{ fontSize: 14 }}
        />
        {isSearching && (
          <CircularProgress size={14} sx={{ ml: 1, flexShrink: 0, color: 'text.disabled' }} />
        )}
      </Box>

      {isFocused && (hasResults || hasNoResults) && (
        <Paper
          elevation={4}
          sx={{
            position: 'absolute',
            top: '100%',
            mt: 0.5,
            width: '100%',
            maxHeight: 400,
            overflow: 'auto',
            zIndex: 1300,
          }}
        >
          {hasNoResults && (
            <Box sx={{ py: 3, textAlign: 'center' }}>
              <Typography
                sx={{ fontFamily: '"VT323", monospace', fontSize: '1.1rem', letterSpacing: 2, color: 'text.disabled' }}
              >
                [ NO RESULTS ]
              </Typography>
            </Box>
          )}

          {results!.users.length > 0 && (
            <Box sx={{ pt: 1 }}>
              <Box sx={{ fontSize: 10, fontWeight: 700, color: 'text.disabled', px: 2, pb: 0.5, letterSpacing: 1 }}>
                USERS
              </Box>
              <Divider />
              {results!.users.map((user) => (
                <Box
                  key={user.id}
                  component={Link}
                  to={`/${user.username}`}
                  onClick={closeResults}
                  sx={{
                    display: 'block',
                    px: 2,
                    py: 0.75,
                    textDecoration: 'none',
                    color: 'text.primary',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <Box component="span" sx={{ fontWeight: 600 }}>@{user.username}</Box>
                  {user.bio && (
                    <Box component="span" sx={{ color: 'text.secondary', fontSize: 12, ml: 1 }}>
                      {user.bio.slice(0, 40)}{user.bio.length > 40 ? '…' : ''}
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          )}

          {results!.feeds.length > 0 && (
            <Box sx={{ pt: 1 }}>
              <Box sx={{ fontSize: 10, fontWeight: 700, color: 'text.disabled', px: 2, pb: 0.5, letterSpacing: 1 }}>
                FEEDS
              </Box>
              <Divider />
              {results!.feeds.map((feed) => (
                <Box
                  key={feed.id}
                  component={Link}
                  to={`/feeds/${feed.feed_tag}`}
                  onClick={closeResults}
                  sx={{
                    display: 'block',
                    px: 2,
                    py: 0.75,
                    textDecoration: 'none',
                    color: 'text.primary',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <Box component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>#{feed.feed_tag}</Box>
                </Box>
              ))}
            </Box>
          )}

          {results!.posts.length > 0 && (
            <Box sx={{ pt: 1, pb: 0.5 }}>
              <Box sx={{ fontSize: 10, fontWeight: 700, color: 'text.disabled', px: 2, pb: 0.5, letterSpacing: 1 }}>
                POSTS
              </Box>
              <Divider />
              {results!.posts.map((post) => {
                const author = getUserRecord(post.user_record_id);
                return (
                  <Box
                    key={post.id}
                    component={Link}
                    to={`/posts/${post.id}`}
                    onClick={closeResults}
                    sx={{
                      display: 'block',
                      px: 2,
                      py: 0.75,
                      textDecoration: 'none',
                      color: 'text.primary',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <Box component="span" sx={{ fontWeight: 600, mr: 1 }}>
                      @{author?.username ?? '…'}
                    </Box>
                    <Box component="span" sx={{ color: 'text.secondary', fontSize: 13 }}>
                      {post.value.slice(0, 60)}
                      {post.value.length > 60 ? '…' : ''}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
}

export default SearchBar;
